import React, { useEffect, useState } from "react";
import {
  Card,
  Container,
  CardContent,
  Typography,
  Grid,
  Divider,
  makeStyles,
  Box,
} from "@material-ui/core";
import mixpanel from "@utils/mixpanel/mixpanel.js";
import MainAnimation from "@components/MainAnimation";
import TCLoading from "../TCLoading";
import { green, grey } from "@material-ui/core/colors";
import { Watermark } from "./Contratos";
import { RELATORIO_ALERTAS_METEREOLOGICOS } from "../../utils/mixpanel/mixpanelEvents";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    margin: "2rem 0 1rem 0",
    fontWeight: "bold",
  },
  card: {
    margin: "1rem",
    color: "#000",
  },
  cardTitle: {
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  infoText: {
    marginBottom: "0.25rem",
  },
  label: {
    fontWeight: "bold",
    //textDecoration: "underline",
    color: grey[500],
    marginRight: 4,
  },
  dividerBetweenInfo: {
    margin: "0.5rem 0",
  },
  sourceText: {
    marginTop: 8,
    fontStyle: "italic",
    fontSize: "0.85rem",
    color: grey[500],
    textAlign: "right",
  },
}));

const allowedStates = [
  "minas gerais",
  "paraná",
  "rio de janeiro",
  "são paulo",
  "bahia",
  "espírito santo",
];

const WeatherAlerts = () => {
  const classes = useStyles();
  const [todayAlerts, setTodayAlerts] = useState([]);
  const [futureAlerts, setFutureAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filterAllowedStates = (alerts) => {
    return alerts.filter((alert) => {
      if (!alert.estados) return false;
      const states = alert.estados
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return allowedStates.some((state) =>
        states.includes(state.normalize("NFD").replace(/[\u0300-\u036f]/g, "")),
      );
    });
  };

  useEffect(() => {
    mixpanel.track(RELATORIO_ALERTAS_METEREOLOGICOS);
    fetch("https://apiprevmet3.inmet.gov.br/avisos/ativos")
      .then((res) => {
        if (!res.ok) throw new Error("Erro ao buscar dados da API");
        return res.json();
      })
      .then((data) => {
        setTodayAlerts(filterAllowedStates(data.hoje || []));
        setFutureAlerts(filterAllowedStates(data.futuro || []));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const renderAlerts = (alerts) =>
    alerts.map((alert) => {
      const descricaoLower = alert.descricao.toLowerCase();
      const isTempestade = descricaoLower.includes("tempestade");
      const isGeada = descricaoLower.includes("geada");

      return (
        <Grid item xs={12} md={6} lg={4} key={alert.id}>
          <Card
            className={classes.card}
            style={{
              backgroundColor: "inherit",
              color: "inherit",
              boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Watermark isSmall />
            <div style={{ position: "relative", zIndex: 10 }}>
              <CardContent>
                <AlertTitle descricao={alert.descricao} className={classes} />
                <Divider className={classes.dividerBetweenInfo} />

                <Typography
                  variant="body2"
                  className={classes.infoText}
                  style={{ textAlign: "justify", marginTop: 4 }}
                >
                  <span className={classes.label}>Publicado em:</span>{" "}
                  {formatDate(alert.created_at)}
                </Typography>

                <Divider className={classes.dividerBetweenInfo} />
                <Typography
                  variant="subtitle2"
                  className={classes.infoText}
                  style={{ textAlign: "justify" }}
                >
                  <span className={classes.label}>Severidade:</span>{" "}
                  <span style={{ color: alert.aviso_cor || "#000" }}>
                    {alert.severidade}
                  </span>
                </Typography>

                <Divider className={classes.dividerBetweenInfo} />

                <Typography
                  variant="body2"
                  className={classes.infoText}
                  style={{ textAlign: "justify" }}
                >
                  <span className={classes.label}>Período:</span>{" "}
                  {formatDate(alert.inicio)} até {formatDate(alert.fim)}
                </Typography>

                <Divider className={classes.dividerBetweenInfo} />

                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: 8,
                    textAlign: "justify",
                  }}
                >
                  <span className={classes.label} style={{ marginRight: 8 }}>
                    Região:
                  </span>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {alert.regioes.split(",").map((regiao, index) => (
                      <span key={index}>{regiao.trim()}</span>
                    ))}
                  </div>
                </div>

                <Divider className={classes.dividerBetweenInfo} />

                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginTop: 8,
                    textAlign: "justify",
                  }}
                >
                  <span className={classes.label} style={{ marginRight: 8 }}>
                    Estados:
                  </span>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {alert.estados.split(",").map((estado, index) => (
                      <span key={index}>{estado.trim()}</span>
                    ))}
                  </div>
                </div>

                {alert.riscos?.length > 0 && (
                  <>
                    <Divider className={classes.dividerBetweenInfo} />
                    <Typography
                      variant="body2"
                      className={classes.infoText}
                      style={{ textAlign: "justify" }}
                    >
                      <span className={classes.label}>Riscos:</span>{" "}
                      {alert.riscos.join(" ")}
                    </Typography>
                  </>
                )}

                {alert.instrucoes?.length > 0 && (
                  <>
                    <Divider className={classes.dividerBetweenInfo} />
                    <Typography
                      variant="body2"
                      className={classes.infoText}
                      style={{ textAlign: "justify" }}
                    >
                      <span className={classes.label}>Instruções:</span>{" "}
                      {alert.instrucoes.join(" ")}
                    </Typography>
                  </>
                )}

                <Typography className={classes.sourceText}>
                  Fonte : inmet.gov.br
                </Typography>
              </CardContent>
            </div>

            {isTempestade && <RainEffect />}
            {isGeada && <FrostEffect />}
          </Card>
        </Grid>
      );
    });

  if (loading)
    return (
      <div
        style={{
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <TCLoading size={40} />
      </div>
    );
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <MainAnimation>
      <Container maxWidth={"lg"} className={classes.containerDiv}>
        {todayAlerts.length > 0 && (
          <>
            <Typography variant="h5" className={classes.sectionTitle}>
              Alertas de Hoje
            </Typography>
            <Grid container spacing={2}>
              {renderAlerts(todayAlerts)}
            </Grid>
          </>
        )}

        {futureAlerts.length > 0 && (
          <>
            <Divider style={{ margin: "2rem 0" }} />
            <Typography variant="h5" className={classes.sectionTitle}>
              Alertas Futuros
            </Typography>
            <Grid container spacing={2}>
              {renderAlerts(futureAlerts)}
            </Grid>
          </>
        )}
      </Container>
    </MainAnimation>
  );
};

export default WeatherAlerts;

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const timeExists = date.getHours() !== 0 || date.getMinutes() !== 0;

  return timeExists
    ? `${day}/${month}/${year} ${hours}:${minutes}`
    : `${day}/${month}/${year}`;
};

const RainEffect = () => {
  return (
    <div
      style={{
        pointerEvents: "none",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: 1,
      }}
    >
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: "2px",
            height: "10px",
            background: "rgba(174,194,224,0.6)",
            animation: "rainFall 1.2s linear infinite",
            animationDelay: `${Math.random()}s`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * -100}%`,
            borderRadius: "50%",
            opacity: 0.7,
          }}
        />
      ))}
      <style>
        {`
          @keyframes rainFall {
            0% {
              transform: translateY(0);
              opacity: 0.7;
            }
            100% {
              transform: translateY(100vh);
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  );
};

const FrostEffect = () => {
  const flakesCount = 13;

  return (
    <div
      style={{
        pointerEvents: "none",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: 1,
        fontSize: 0,
      }}
    >
      {[...Array(flakesCount)].map((_, i) => {
        const size = 10 + Math.random() * 8; // 12-24px
        const left = Math.random() * 100; // %
        const delay = Math.random() * 2; // s
        const duration = 5 + Math.random() * 2;
        const opacity = 0.4 + Math.random() * 0.6;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: "-20px",
              left: `${left}%`,
              fontSize: `${size}px`,
              opacity,
              animation: `snowFall${i} ${duration}s linear infinite`,
              animationDelay: `${delay}s`,
              userSelect: "none",
            }}
          >
            ❄️
            <style>
              {`
                @keyframes snowFall${i} {
                  0% {
                    transform: translateY(0) translateX(0);
                    opacity: ${opacity};
                  }
                  25% {
                    transform: translateY(25vh) translateX(10px);
                    opacity: ${opacity * 0.7};
                  }
                  50% {
                    transform: translateY(50vh) translateX(-10px);
                    opacity: ${opacity * 0.9};
                  }
                  75% {
                    transform: translateY(75vh) translateX(10px);
                    opacity: ${opacity * 0.7};
                  }
                  100% {
                    transform: translateY(100vh) translateX(0);
                    opacity: 0;
                  }
                }
              `}
            </style>
          </div>
        );
      })}
    </div>
  );
};

const AlertTitle = ({ descricao, className }) => {
  const keywordEmojiMap = {
    tempestade: "⛈️",
    geada: "❄️",
    "chuvas intensas": "🌧️",
    "acumulado de chuva": "💧",
    "declínio de temperatura": "📉🌡️",
    "baixa umidade": "🌬️",
    "declinio de temperatura": "📉🌡️",
    "onda de frio": "🥶",
  };

  const descricaoLower = descricao
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const emoji = Object.entries(keywordEmojiMap).find(([keyword]) => {
    console.log("descricaoLower", descricaoLower);
    return descricaoLower.includes(keyword);
  })?.[1];

  return (
    <Typography variant="h6" className={className}>
      {emoji && (
        <Box component="span" sx={{ marginRight: "8px" }}>
          <span title={descricao}>{emoji}</span>
        </Box>
      )}
      {descricao}
    </Typography>
  );
};
